import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { AccountDeletionService, PENDING_DELETION_ROUTE } from 'src/app/service/account-deletion.service';
import { AuthService } from 'src/app/service/auth.service';
import { formatDeletionDate } from './deletion-date.util';

/**
 * Tela de desistência (/account/pending-deletion), mostrada a quem tem a conta em carência.
 * Fora do shell e sem chamar nenhum outro endpoint: tudo fora de /user/{id}/deletion e
 * /global/ devolve 403 durante a carência.
 */
@Component({
  selector: 'app-account-deletion-pending',
  templateUrl: './account-deletion-pending.component.html',
  styleUrl: './account-deletion.component.css',
})
export class AccountDeletionPendingComponent implements OnInit {
  loading = true;
  cancelling = false;
  scheduledDate = '';

  private userId = '';

  constructor(
    private router: Router,
    private authService: AuthService,
    private accountDeletion: AccountDeletionService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.userId = this.authService.getUserInformations() ?? '';
    // Enquanto o GET não volta, a data do flag (se já conhecida) evita a tela vazia.
    this.scheduledDate = formatDeletionDate(this.accountDeletion.pendingScheduledAt);

    // O GET é a fonte da verdade: a pessoa pode ter desistido em outro aparelho.
    this.accountDeletion.getStatus(this.userId).subscribe({
      next: (status) => {
        if (status.status === 'active') {
          this.accountDeletion.clearPending();
          this.goToApp();
          return;
        }
        this.accountDeletion.markPending(status.scheduledAt);
        this.scheduledDate = formatDeletionDate(status.scheduledAt);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showMessage('error', 'user.accountDeletion.genericError');
      },
    });
  }

  giveUp(): void {
    if (this.cancelling) return;
    this.cancelling = true;
    this.accountDeletion.cancel(this.userId).subscribe({
      next: () => this.onCancelled(),
      error: (err: HttpErrorResponse) => {
        // Já havia desistido em outro aparelho: nada a fazer, segue para o app.
        if (err.status === 404 && err.error?.error === 'no_pending_deletion') {
          this.onCancelled();
          return;
        }
        this.cancelling = false;
        this.showMessage('error', 'user.accountDeletion.genericError');
      },
    });
  }

  logout(): void {
    void this.authService.logout();
  }

  private onCancelled(): void {
    this.accountDeletion.clearPending();
    this.showMessage('success', 'user.accountDeletion.cancelled');
    this.goToApp();
  }

  /** Retoma o destino guardado no login (deixado intacto na carência); senão, Minha Garagem. */
  private goToApp(): void {
    const redirect = this.authService.consumePostLoginRedirect();
    if (redirect && redirect.split(/[?#]/)[0] !== PENDING_DELETION_ROUTE) {
      this.router.navigateByUrl(redirect);
      return;
    }
    this.router.navigate(['/my-garage']);
  }

  private showMessage(severity: 'success' | 'error', key: string): void {
    this.messageService.add({ severity, summary: this.translocoService.translate(key) });
  }
}
